/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { SuspensionCategory } from '@linc-technologies/linced-common/enums/suspensions';
import moment from 'moment';
import {
  BehaviourType,
  IncidentOutcome,
  IncidentStatus,
  BehaviourCategoriesNSW,
} from 'linced-app/enums/behaviours';
import type Expulsion from 'linced-app/models/attendance/expulsion';
import type Suspension from 'linced-app/models/attendance/suspension';
import type Person from 'linced-app/models/person';
import type Post from 'linced-app/models/posts/post';
import type School from 'linced-app/models/school';

// Proto: attendance.BehaviourIncident.
export default class BehaviourIncident extends Model {
  declare [Type]: 'attendance/behaviour-incident';

  // externalId is the behaviour incident's id in a 3rd party database
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the behaviour incident is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // date contains the date the incident was recorded against as a
  // string to facilitate filtering.
  @attr('number')
  declare date: number | undefined;

  // authorId is the unique identifier of the person who marked the incident
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  // learnerId is the unique identifier of the learner who the incident is about.
  @belongsTo('person', { async: true, inverse: null })
  declare learnerId: AsyncBelongsTo<Person>;

  @alias('learnerId')
  declare learner: AsyncBelongsTo<Person>;

  @alias('learner.id')
  declare learner_id: string | null | undefined;

  // reportedBy is the unique identifier of the person who reported the incident
  @belongsTo('person', { async: true, inverse: null })
  declare reportedBy: AsyncBelongsTo<Person>;

  // jurisdiction specifies the educational jurisdiction the incident should be
  // considered under
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // category specifies the incidents category
  @attr('number', { defaultValue: 0 })
  declare category: number;

  // customCategory stores the chosen school defined category
  @attr('string', { defaultValue: '' })
  declare customCategory: string;

  // type specifies the incidents type
  @attr('number', { defaultValue: 0 })
  declare type: number;

  // entryNote provides any notes from when the incident was recorded
  @attr('string', { defaultValue: '' })
  declare entryNote: string;

  // outcome is an enum value that specifies the outcome of an incident
  @attr('number', { defaultValue: 0 })
  declare outcome: number;

  // outcomeDescription is a written note giving detail on the outcome of an incident
  @attr('string', { defaultValue: '' })
  declare outcomeDescription: string;

  // referredTo is the person who the behaviour incident was referred to (if any)
  @belongsTo('person', { async: true, inverse: null })
  declare referredTo: AsyncBelongsTo<Person>;

  // referralNote is any details passed on to the person the incident was referred to
  @attr('string', { defaultValue: '' })
  declare referralNote: string;

  // status is the current status of the incident
  @attr('number', { defaultValue: 0 })
  declare status: number;

  // postId is the unique identifier of the post entity associated with this sickbay visit
  @belongsTo('posts/post', { async: true, inverse: null })
  declare postId: AsyncBelongsTo<Post>;

  @alias('postId')
  declare post: AsyncBelongsTo<Post>;

  // suspensionId is the unique identifier of the suspension or formal caution that resulted from the
  // incident (if there was one)
  @belongsTo('attendance/suspension', { async: true, inverse: 'incidentId' })
  declare suspensionId: AsyncBelongsTo<Suspension>;

  @alias('suspensionId')
  declare suspension: AsyncBelongsTo<Suspension>;

  // expulsionId is the unique identifier of the expulsion that resulted from the incident (if there was one)
  @belongsTo('attendance/expulsion', { async: true, inverse: 'incidentId' })
  declare expulsionId: AsyncBelongsTo<Expulsion>;

  @alias('expulsionId')
  declare expulsion: AsyncBelongsTo<Expulsion>;

  // timePeriod is the time period when the incident occurred
  @attr('number', { defaultValue: 0 })
  declare timePeriod: number;

  @computed('date')
  get displayDate() {
    // TS-ZERO(date)
    return moment.unix(this.date!).format('dddd <br> YYYY&#8209;MM&#8209;DD');
  }

  @computed('date')
  get displayTime() {
    // TS-ZERO(date)
    return moment.unix(this.date!).format('h:mm A');
  }

  @computed('date')
  get filterDate() {
    // TS-ZERO(date)
    return moment.unix(this.date!).format('dddd YYYY-MM-DD');
  }

  @computed('category', 'customCategory', 'jurisdiction')
  get displayCategory() {
    if (!this.category) {
      return '';
    }
    if (this.category === BehaviourCategoriesNSW.OTHER) {
      return this.customCategory;
    }
    return BehaviourCategoriesNSW.getLabel(this.category);
  }

  @computed(
    'outcome',
    'suspension.{id,category,endDate}',
    'expulsion.{id,displayStage}',
  )
  get displayOutcome() {
    switch (this.outcome) {
      case IncidentOutcome.SUSPENSION:
        if (!isEmpty(this.suspension) && !isEmpty(this.get('suspension.id'))) {
          const category = this.get(
            'suspension.category',
          ) as Suspension['category'];
          if (category === SuspensionCategory.FORMAL_CAUTION) {
            if (
              moment(
                this.get('suspension.endDate') as Suspension['endDate'],
                'YYYY-MM-DD',
              ).isAfter(moment())
            ) {
              return 'Active Formal Caution';
            }
            return 'Expired Formal Caution';
          }
          if (category > SuspensionCategory.FORMAL_CAUTION) {
            return 'Suspension';
          }
          // Behaviour incidents with custom school set categories can only become formal cautions
          return this.category === BehaviourCategoriesNSW.OTHER
            ? 'Pending Formal Caution'
            : 'Pending Suspension/Formal Caution';
        }
        break;
      case IncidentOutcome.EXPULSION:
        if (!isEmpty(this.expulsion) && !isEmpty(this.get('expulsion.id'))) {
          return this.get('expulsion.displayStage');
        }
        break;
    }
    return IncidentOutcome.getLabel(this.outcome);
  }

  @computed('status')
  get displayStatus() {
    return IncidentStatus.getLabel(this.status);
  }

  @computed('type')
  get displayType() {
    return BehaviourType.getLabel(this.type);
  }
}
